import { CalendarCheck, ShoppingCart, BookMarked } from "lucide-react";
import { RedactedText } from "@/components/redaction/RedactedText";
import { StorePicker } from "@/components/StorePicker";
import { useStore } from "@/context/StoreContext";
import { BOOKS, SITE, SCHEDULE_NOTE } from "@/data/series";

const statusTone = (status) => {
  if (status === "DECLASSIFIED") return "stamp stamp-open";
  if (status === "CLASSIFIED") return "stamp";
  return "stamp stamp-neutral";
};

export const StatusSection = () => {
  const { code, kindleUrl, paperbackUrl, urlFor } = useStore();
  const bookTwo = BOOKS.find((b) => b.number === 2);
  const bookTwoUrl = bookTwo?.asin ? urlFor(bookTwo.asin) : SITE.bookTwoUrl;

  return (
  <section
    id="status"
    data-testid="status-section"
    className="relative z-10 scroll-mt-20 border-b border-border"
  >
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-7">
          <p className="eyebrow">{SCHEDULE_NOTE.kicker}</p>
          <h2 className="mt-3 text-3xl font-semibold leading-tight sm:text-[2.6rem]">
            Publication schedule
          </h2>

          <div
            className="panel mt-8 overflow-hidden"
            data-testid="release-status-table"
          >
            <div className="grid grid-cols-[2.5rem_1fr_auto] gap-3 border-b border-border bg-[hsl(220_18%_8%)] px-4 py-2.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                No.
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                Title
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                Release
              </span>
            </div>

            {BOOKS.map((book) => (
              <div
                key={book.number}
                data-testid={`schedule-row-${book.number}`}
                className="grid grid-cols-[2.5rem_1fr_auto] items-center gap-3 border-b border-border px-4 py-3.5 last:border-b-0 transition-colors duration-150 hover:bg-white/[0.02]"
              >
                <span className="font-mono text-[11px] text-muted-foreground">
                  {book.numeral}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">
                    {book.title || (
                      <span className="redact px-1">██████████</span>
                    )}
                  </span>
                  <span className={`${statusTone(book.status)} mt-1.5 inline-block`}>
                    {book.status}
                  </span>
                </span>
                <span
                  className={`whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.12em] ${
                    book.released
                      ? "text-[hsl(174_62%_64%)]"
                      : "text-[hsl(215_10%_58%)]"
                  }`}
                >
                  {book.releaseDate || SITE.dateplaceholder}
                </span>
              </div>
            ))}
          </div>

          <p className="mt-4 font-mono text-[10px] uppercase leading-relaxed tracking-[0.14em] text-[hsl(215_10%_52%)]">
            {SCHEDULE_NOTE.finalLine}
          </p>
        </div>

        <div className="lg:col-span-5">
          <div className="paper h-full p-5 sm:p-7">
            <span className="inline-flex items-center gap-2 rounded-[6px] border border-[hsl(174_62%_30%/0.5)] bg-[hsl(174_62%_30%/0.08)] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(174_62%_24%)]">
              <CalendarCheck className="h-3 w-3" />
              Dates confirmed
            </span>

            <h3 className="mt-5 text-2xl font-semibold leading-tight text-[hsl(var(--ink))]">
              {SCHEDULE_NOTE.heading}
            </h3>

            <p className="mt-4 text-[14.5px] leading-relaxed text-[hsl(220_14%_26%)]">
              {SCHEDULE_NOTE.body}
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a
                href={kindleUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="status-buy-link"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-[10px] bg-[hsl(220_18%_10%)] px-5 font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(38_38%_94%)] transition-colors duration-150 hover:bg-[hsl(174_62%_26%)]"
              >
                <ShoppingCart className="h-3.5 w-3.5" />
                {SITE.buyLabel}
                <span aria-hidden="true" className="text-[hsl(38_20%_72%)]">
                  · {code}
                </span>
              </a>

              <a
                href={paperbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="status-paperback-link"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-[10px] border border-[hsl(36_20%_70%)] px-5 font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(220_18%_18%)] transition-colors duration-150 hover:bg-[hsl(36_24%_90%)]"
              >
                <BookMarked className="h-3.5 w-3.5" />
                Paperback
              </a>

              <a
                href="#waitlist"
                data-testid="status-join-link"
                className="inline-flex h-11 items-center justify-center rounded-[10px] border border-[hsl(36_20%_70%)] px-5 font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(220_18%_18%)] transition-colors duration-150 hover:bg-[hsl(36_24%_90%)]"
              >
                Get release alerts
              </a>
            </div>

            <div className="mt-3">
              <StorePicker tone="ink" className="-ml-1.5" />
            </div>

            <div className="mt-6 border-t border-[hsl(36_16%_80%)] pt-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(220_12%_38%)]">
                Newly declassified — Book Two
              </p>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[hsl(220_14%_30%)]">
                <em>The Weight of Blood</em> is out now in ebook and paperback.
                The file stays redacted; the book does not.
              </p>
              <a
                href={bookTwoUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="status-book-two-link"
                className="mt-3 inline-flex h-11 items-center justify-center gap-2 rounded-[10px] bg-[hsl(220_18%_10%)] px-5 font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(38_38%_94%)] transition-colors duration-150 hover:bg-[hsl(174_62%_26%)]"
              >
                <ShoppingCart className="h-3.5 w-3.5" />
                {SITE.bookTwoLabel}
                <span aria-hidden="true" className="text-[hsl(38_20%_72%)]">
                  · {code}
                </span>
              </a>
            </div>

            <div className="mt-8 border-t border-[hsl(36_16%_80%)] pt-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(220_12%_38%)]">
                Addendum — Book Six
              </p>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[hsl(220_14%_30%)]">
                <RedactedText text={SCHEDULE_NOTE.sixth} tone="ink" />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};
